import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { RedeemPage } from "./pages/RedeemPage";
import { PackagesIndexPage } from "./pages/PackagesIndexPage";
import { PackageView } from "./pages/PackageView";
import { ItemPage } from "./pages/ItemPage";
import { BrokerApplyPage } from "./pages/BrokerApplyPage";
import { BrokerResubmitPage } from "./pages/BrokerResubmitPage";
import { BrokerLoginPage } from "./pages/BrokerLoginPage";
import { BrokerPasswordResetPage } from "./pages/BrokerPasswordResetPage";
import { BrokerDashboardPage } from "./pages/BrokerDashboardPage";
import { LeadGeneratorDashboardPage } from "./pages/LeadGeneratorDashboardPage";
import { BrokerageDashboardPage } from "./pages/BrokerageDashboardPage";
import { BrokerageSubBrokerDetailPage } from "./pages/BrokerageSubBrokerDetailPage";
import { BrokerCoursePage } from "./pages/BrokerCoursePage";
import { BrokerMaterialsPage } from "./pages/BrokerMaterialsPage";
import { BrokerDealStatusPage } from "./pages/BrokerDealStatusPage";
import { DealSubmissionWizardEntry } from "./pages/deal-submission/DealSubmissionWizardEntry";
import { DealSubmissionRunner } from "./pages/deal-submission/DealSubmissionRunner";
import { RecipientDashboardPage } from "./pages/RecipientDashboardPage";
import { PortalSetPasswordPage } from "./pages/PortalSetPasswordPage";
import { PrequalifyLandingPage } from "./pages/prequalify/PrequalifyLandingPage";
import { BuyerPrequalifyPage } from "./pages/prequalify/BuyerPrequalifyPage";
import { SellerPrequalifyPage } from "./pages/prequalify/SellerPrequalifyPage";
import { PrequalResultPage } from "./pages/prequalify/PrequalResultPage";
import { DealExplorerPage } from "./pages/prequalify/DealExplorerPage";
import { PortalAssistPrequalPage } from "./pages/prequalify/PortalAssistPrequalPage";
import { PrequalBrandProvider } from "./pages/prequalify/PrequalBrandContext";

// External Intake & Collection v1.0 PR 4 — the external party portal SPA.
// Mounted under /portal/* from main.tsx so the staff <AuthProvider> and
// its /api/auth/me call never fire for a recipient. Every route here
// uses the portal HTTP client, the portal auth context, and the portal
// renderer — none of which touch staff endpoints.
//
// PR 9b §8 adds the broker surface: a public application page, the
// broker login + password-reset flow, and the authenticated broker
// dashboard + course + materials + deals routes.

/** Redirect a legacy /portal/prequalify* URL to its /prequalify* equivalent,
 *  preserving the query string (e.g. ?view=documents). */
function LegacyPrequalRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export function PortalApp() {
  return (
    <Routes>
      <Route path="/portal/apply" element={<BrokerApplyPage />} />
      <Route path="/portal/apply/resubmit/:token" element={<BrokerResubmitPage />} />
      <Route path="/portal/login" element={<BrokerLoginPage />} />
      <Route path="/portal/password-reset" element={<BrokerPasswordResetPage />} />
      {/* Password-reset emails link here (PortalAuth:PasswordResetLinkBaseUrl).
          Reuses the same page, which reads ?token= and confirms the reset. */}
      <Route path="/portal/reset-password" element={<BrokerPasswordResetPage />} />
      {/* External Portal v2 §3.2 — invited / migrated recipients bind a password here. */}
      <Route path="/portal/set-password" element={<PortalSetPasswordPage />} />

      {/* Prequalification System v1 — the public, anonymous prequal funnel
          (role fork + buyer/seller tracks). Canonical at the top-level
          /prequalify (native) and /prequalify/{slug} (white-label partner);
          the legacy /portal/prequalify* URLs now redirect here.

          The static native role routes are declared BEFORE the /:slug routes
          so buyer / seller / explore / result always win over a partner slug
          (React Router also ranks static above dynamic — belt and braces). */}
      {/* The partner brand is resolved ONCE for the whole flow by this pathless
          layout route, and inherited by every surface beneath it. Per-route
          resolution is what made branding opt-in — a screen added to this flow
          was unbranded by default until a partner noticed.

          It renders NO DOM (context + <Outlet/>), so it changes nothing about
          the markup and may legally sit above the assisted operator strip,
          which must stay outside the theme WRAPPER.

          The assist route is inside it deliberately: the layout instance
          survives the navigation from the assisted console to the result
          screen, even though the two sit under different path prefixes. */}
      <Route element={<PrequalBrandProvider />}>
        <Route path="/prequalify" element={<PrequalifyLandingPage />} />
        <Route path="/prequalify/buyer" element={<BuyerPrequalifyPage />} />
        <Route path="/prequalify/buyer/result" element={<PrequalResultPage role="Buyer" />} />
        <Route path="/prequalify/seller" element={<SellerPrequalifyPage />} />
        <Route path="/prequalify/seller/result" element={<PrequalResultPage role="Seller" />} />
        <Route path="/prequalify/explore" element={<DealExplorerPage />} />
        {/* White-label partner funnel — same pages, resolved by :slug. */}
        <Route path="/prequalify/:slug" element={<PrequalifyLandingPage />} />
        <Route path="/prequalify/:slug/buyer" element={<BuyerPrequalifyPage />} />
        <Route path="/prequalify/:slug/buyer/result" element={<PrequalResultPage role="Buyer" />} />
        <Route path="/prequalify/:slug/seller" element={<SellerPrequalifyPage />} />
        <Route path="/prequalify/:slug/seller/result" element={<PrequalResultPage role="Seller" />} />
        <Route path="/prequalify/:slug/explore" element={<DealExplorerPage />} />

        {/* Assisted completion — a signed-in broker drives a client's form, in
            the same sitting, on their own machine.

            Deliberately NOT under /prequalify: those paths are the ANONYMOUS
            funnel and are addressed by token, and hanging a session-authenticated,
            id-addressed route off the same prefix is how the two credential models
            end up sharing a page. Under /portal/prequal-site it sits beside the
            reads it belongs to, and the funnel's own post-finalize navigation
            still lands on /prequalify/{role}/result, which this router already
            serves. */}
        <Route path="/portal/prequal-site/assist/:draftId" element={<PortalAssistPrequalPage />} />
      </Route>

      {/* Legacy mount — redirect to the canonical /prequalify* paths. */}
      <Route path="/portal/prequalify" element={<LegacyPrequalRedirect to="/prequalify" />} />
      <Route path="/portal/prequalify/buyer" element={<LegacyPrequalRedirect to="/prequalify/buyer" />} />
      <Route
        path="/portal/prequalify/buyer/result"
        element={<LegacyPrequalRedirect to="/prequalify/buyer/result" />}
      />
      <Route path="/portal/prequalify/seller" element={<LegacyPrequalRedirect to="/prequalify/seller" />} />
      <Route
        path="/portal/prequalify/seller/result"
        element={<LegacyPrequalRedirect to="/prequalify/seller/result" />}
      />
      <Route path="/portal/prequalify/explore" element={<LegacyPrequalRedirect to="/prequalify/explore" />} />

      <Route path="/portal/broker" element={<BrokerDashboardPage />} />
      <Route path="/portal/broker/course" element={<BrokerCoursePage />} />
      <Route path="/portal/broker/materials" element={<BrokerMaterialsPage />} />
      {/* The standalone "My deals" list was a duplicate of the broker
          dashboard's "Deals & status" tab; it now redirects there so old
          bookmarks / deep links / the back button still land somewhere
          correct. Don't reintroduce a second deals-list screen. */}
      <Route
        path="/portal/broker/deals"
        element={<Navigate to="/portal/broker?tab=deals" replace />}
      />
      <Route path="/portal/broker/deals/new" element={<DealSubmissionWizardEntry />} />
      <Route
        path="/portal/broker/deals/submissions/:id"
        element={<DealSubmissionRunner />}
      />
      <Route
        path="/portal/broker/deals/submissions/:id/status"
        element={<BrokerDealStatusPage />}
      />

      {/* Remediation Tier 2 #9 (Prompt 9-ii) — per-type recipient
          shells. The shared RecipientDashboardPage renders the
          assignments / documents / materials / notifications modules;
          the audience prop gates the route by AccountType (a buyer
          landing on /portal/seller is bounced back to their own). */}
      <Route path="/portal/buyer" element={<RecipientDashboardPage audience="Buyer" />} />
      <Route path="/portal/seller" element={<RecipientDashboardPage audience="Seller" />} />
      <Route path="/portal/vendor" element={<RecipientDashboardPage audience="Vendor" />} />
      {/* TPS Servicing Portal PR-C — the third-party servicer's portal. */}
      <Route path="/portal/tps" element={<RecipientDashboardPage audience="Tps" />} />
      {/* Lead Generation System — the external Lead Generator console
          (dedicated page, mirroring the broker shell). */}
      <Route path="/portal/lead-generator" element={<LeadGeneratorDashboardPage />} />
      {/* Brokerage / Sub-Broker portal — the external brokerage firm's console
          (dedicated page, mirroring the broker shell). */}
      <Route path="/portal/brokerage" element={<BrokerageDashboardPage />} />
      {/* Sub-broker detail — opened from a Roster row. A dedicated sub-route (not
          a rail tab) so it's deep-linkable; renders in the same chrome with the
          Sub-brokers rail entry highlighted. */}
      <Route
        path="/portal/brokerage/sub-brokers/:subBrokerId"
        element={<BrokerageSubBrokerDetailPage />}
      />

      <Route path="/portal/r/:token?" element={<RedeemPage />} />
      <Route path="/portal" element={<PackagesIndexPage />} />
      <Route path="/portal/packages/:packageId" element={<PackageView />} />
      <Route path="/portal/packages/:packageId/items/:itemId" element={<ItemPage />} />
      <Route path="/portal/*" element={<Navigate to="/portal" replace />} />
    </Routes>
  );
}
